/**
 * Reglas de asignación del CRM comercial ("cliente propio").
 *
 * Un prospecto (`prospectos_crm/{padron}`) asignado a un agente queda BLOQUEADO
 * durante 60 días corridos contados desde su ÚLTIMA GESTIÓN registrada. Vencido
 * ese plazo sin gestión, se considera libre y puede tomarlo cualquier otro
 * agente (la limpieza definitiva del campo la hace una Cloud Function diaria;
 * acá calculamos el estado "en vivo" para la UI).
 *
 * Campos relevantes del doc:
 *   asignadoEmail, asignadoA, asignadoAt, ultimaGestionAt, bloqueadoHasta,
 *   asignacionHistorial[]
 */
import { Timestamp } from "firebase/firestore";
import { toJSDate } from "./dates";
import { contactosDeCrm } from "./crmContactos";

export const DIAS_LOCK = 60;
const MS_DAY = 24 * 60 * 60 * 1000;
const MS_LOCK = DIAS_LOCK * MS_DAY;

const SUPERADMIN = 2;
const COMERCIAL = 4;

/** `bloqueadoHasta` = fromDate + 60 días, como Firestore Timestamp. */
export function computeBloqueadoHasta(fromDate = new Date()) {
  const base = fromDate instanceof Date ? fromDate : toJSDate(fromDate) || new Date();
  return Timestamp.fromDate(new Date(base.getTime() + MS_LOCK));
}

/** ¿el prospecto tiene un agente asignado (campo no vacío)? */
export function estaAsignado(crm) {
  return !!(crm && crm.asignadoEmail);
}

/** Fecha de liberación (Date) o null si no aplica / doc legacy sin el campo. */
export function fechaLiberacion(crm) {
  if (!crm) return null;
  return toJSDate(crm.bloqueadoHasta) || null;
}

/** ¿bloqueado ahora? = asignado && now < bloqueadoHasta. Legacy sin fecha ⇒ no bloqueado. */
export function estaBloqueado(crm, now = Date.now()) {
  if (!estaAsignado(crm)) return false;
  const lib = fechaLiberacion(crm);
  if (!lib) return false;
  return now < lib.getTime();
}

/** ¿libre para tomar? = sin asignar, o asignado pero vencido. */
export function estaLibre(crm, now = Date.now()) {
  return !estaAsignado(crm) || !estaBloqueado(crm, now);
}

/** Días corridos restantes del bloqueo (>=0). */
export function diasRestantesLock(crm, now = Date.now()) {
  const lib = fechaLiberacion(crm);
  if (!lib) return 0;
  const ms = lib.getTime() - now;
  return ms > 0 ? Math.ceil(ms / MS_DAY) : 0;
}

/** ¿el email es el titular (agente asignado)? Comparación case-insensitive. */
export function esTitular(crm, email) {
  if (!crm || !crm.asignadoEmail || !email) return false;
  return String(crm.asignadoEmail).toLowerCase() === String(email).toLowerCase();
}

/**
 * ¿Puede el usuario TOMAR/asignar este prospecto?
 *   - SuperAdmin: siempre (override, aun bloqueado).
 *   - Comercial: solo si está libre, o si ya es suyo.
 */
export function puedeTomar(crm, { email, userType }, now = Date.now()) {
  const t = Number(userType);
  if (t === SUPERADMIN) return true;
  if (t === COMERCIAL) return estaLibre(crm, now) || esTitular(crm, email);
  return false;
}

/**
 * ¿Puede EDITAR los campos CRM (estado, contacto, próxima acción, notas)?
 * Mismo criterio que tomar: SuperAdmin siempre; comercial si es suyo o está libre
 * (al guardar se auto-reclama).
 */
export function puedeEditar(crm, { email, userType }, now = Date.now()) {
  return puedeTomar(crm, { email, userType }, now);
}

/**
 * ¿El guardado incluye una GESTIÓN concreta que reinicia el reloj de 60 días?
 * Cuenta: cambio de estado, nueva nota, o cambio en contactos / próxima acción.
 * NO cuenta: asignarse el prospecto ni cambios de prioridad u otros triviales.
 * `contactosDeCrm` normaliza tanto el arreglo nuevo como el contacto legacy, así
 * funciona igual para edificios (multi-contacto) y para admin/desarrolladoras.
 */
export function esGestion(prevCrm, form, nuevaNota) {
  const prev = prevCrm || {};
  if (nuevaNota && nuevaNota.trim()) return true;
  if ((form.estado || "sin_contactar") !== (prev.estado || "sin_contactar")) return true;
  const norm = (x) => JSON.stringify(contactosDeCrm(x).map((c) => [c.nombre, c.rol, c.tel, c.email]));
  if (norm(form) !== norm(prev)) return true;
  const campos = ["proximaAccion", "proximaAccionFecha", "proximaAccionHora"];
  return campos.some((k) => (form[k] || "") !== (prev[k] || ""));
}

/** Fecha de liberación formateada en es-UY (o null). */
export function fmtFechaLiberacion(crm) {
  const d = fechaLiberacion(crm);
  return d ? d.toLocaleDateString("es-UY", { day: "numeric", month: "short", year: "numeric" }) : null;
}
